import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Users, DollarSign, MessageCircle, BarChart3, Clock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      if (res.data.success) {
        localStorage.setItem("admin_token", res.data.token);
        setToken(res.data.token);
      }
    } catch {
      setLoginError("Invalid credentials");
    }
  };

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/admin/metrics`, { headers: { "X-Admin-Token": token } })
      .then(res => { if (res.data.success) setMetrics(res.data); })
      .catch(() => { setToken(""); localStorage.removeItem("admin_token"); })
      .finally(() => setLoading(false));
  }, [token]);

  if (!token) {
    return (
      <div className="flex items-center justify-center h-screen px-4">
        <div className="glass rounded-2xl p-6 w-full max-w-sm border border-white/10">
          <h1 className="font-cinzel text-xl text-amber-400 font-bold mb-4 text-center">Admin Login</h1>
          <div className="space-y-3">
            <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" className="bg-white/5 border-white/10 text-slate-100" />
            <Input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password"
              className="bg-white/5 border-white/10 text-slate-100" onKeyDown={e => e.key === "Enter" && handleLogin()} />
            {loginError && <p className="text-xs text-red-400">{loginError}</p>}
            <Button onClick={handleLogin} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold">Login</Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="h-8 w-8 text-amber-400 animate-spin" /></div>;

  const m = metrics?.metrics || {};

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-cinzel text-2xl text-amber-400 font-bold">Admin Dashboard</h1>
        <Button variant="outline" size="sm" onClick={() => { setToken(""); localStorage.removeItem("admin_token"); }}
          className="text-xs border-red-500/30 text-red-400">Logout</Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total Users", value: m.total_users, icon: Users, color: "text-blue-400" },
          { label: "Revenue", value: `$${m.total_revenue || 0}`, icon: DollarSign, color: "text-emerald-400" },
          { label: "Paid Txns", value: m.paid_transactions, icon: BarChart3, color: "text-violet-400" },
          { label: "Saved Sessions", value: m.total_saved_sessions, icon: MessageCircle, color: "text-amber-400" },
        ].map(s => (
          <Card key={s.label} className="bg-black/30 border-white/10">
            <CardContent className="p-4 text-center">
              <s.icon className={`h-5 w-5 mx-auto mb-1 ${s.color}`} />
              <p className="text-xl font-bold text-white">{s.value ?? "-"}</p>
              <p className="text-[10px] text-slate-500">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Mode breakdown */}
      <div className="glass rounded-xl p-4 mb-6 border border-white/5">
        <h2 className="text-sm font-bold text-slate-300 mb-2">User Modes</h2>
        <div className="flex gap-4">
          <div className="flex-1 text-center p-2 rounded-lg bg-amber-500/10">
            <p className="text-lg font-bold text-amber-400">{m.simple_mode_users || 0}</p>
            <p className="text-[10px] text-slate-500">Simple Mode</p>
          </div>
          <div className="flex-1 text-center p-2 rounded-lg bg-violet-500/10">
            <p className="text-lg font-bold text-violet-400">{m.astrologer_mode_users || 0}</p>
            <p className="text-[10px] text-slate-500">Astrologer Mode</p>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="glass rounded-xl p-4 mb-6 border border-white/5 overflow-x-auto">
        <h2 className="text-sm font-bold text-slate-300 mb-3">Users ({metrics?.users?.length || 0})</h2>
        <table className="w-full text-xs">
          <thead><tr className="text-slate-500 border-b border-white/5">
            <th className="text-left p-2">Name</th><th className="text-left p-2">Email</th>
            <th className="p-2">Mode</th><th className="p-2">Credits</th><th className="p-2">Unlimited</th><th className="p-2">Joined</th>
          </tr></thead>
          <tbody>
            {(metrics?.users || []).map(u => (
              <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5">
                <td className="p-2 text-slate-200">{u.name || "-"}</td>
                <td className="p-2 text-slate-400">{u.email}</td>
                <td className="p-2 text-center"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${u.mode === "astrologer" ? "bg-violet-500/15 text-violet-400" : "bg-amber-500/15 text-amber-400"}`}>{u.mode || "simple"}</span></td>
                <td className="p-2 text-center text-slate-300">{u.readings_purchased || 0}</td>
                <td className="p-2 text-center">
                  <button onClick={async () => {
                    const newVal = !u.unlimited;
                    await axios.post(`${API}/admin/toggle-unlimited`, { user_id: u.user_id, unlimited: newVal }, { headers: { "X-Admin-Token": token } });
                    setMetrics(prev => ({ ...prev, users: prev.users.map(x => x.user_id === u.user_id ? { ...x, unlimited: newVal } : x) }));
                  }} className={`w-9 h-5 rounded-full transition-all relative ${u.unlimited ? "bg-emerald-500" : "bg-slate-700"}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${u.unlimited ? "left-[18px]" : "left-0.5"}`} />
                  </button>
                </td>
                <td className="p-2 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent Activity */}
      <div className="glass rounded-xl p-4 mb-6 border border-white/5">
        <h2 className="text-sm font-bold text-slate-300 mb-3 flex items-center gap-2"><Clock className="h-4 w-4" /> Recent Activity ({metrics?.activity?.length || 0})</h2>
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {(metrics?.activity || []).map((a, i) => (
            <div key={i} className="flex items-start gap-2 py-1.5 border-b border-white/5 last:border-0">
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0 ${a.mode === "compatibility" ? "bg-pink-500/15 text-pink-400" : "bg-blue-500/15 text-blue-400"}`}>{a.mode || "?"}</span>
              <p className="text-[11px] text-slate-300 flex-1 truncate">{a.question}</p>
              <span className="text-[9px] text-slate-600 shrink-0">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ""}</span>
            </div>
          ))}
          {(!metrics?.activity || metrics.activity.length === 0) && <p className="text-xs text-slate-600 text-center py-4">No activity logged yet</p>}
        </div>
      </div>

      {/* Recent Payments */}
      <div className="glass rounded-xl p-4 border border-white/5">
        <h2 className="text-sm font-bold text-slate-300 mb-3">Recent Payments</h2>
        <div className="space-y-1">
          {(metrics?.payments || []).map((p, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
              <span className="text-xs text-slate-300">{p.package_id}</span>
              <span className="text-xs font-bold text-emerald-400">${p.amount}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${p.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-yellow-500/15 text-yellow-400"}`}>{p.payment_status}</span>
              <span className="text-[9px] text-slate-600">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}</span>
            </div>
          ))}
          {(!metrics?.payments || metrics.payments.length === 0) && <p className="text-xs text-slate-600 text-center py-4">No payments yet</p>}
        </div>
      </div>
    </div>
  );
}
